import { Inventory, Sync } from "@mui/icons-material";
import {
  Box,
  Skeleton,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridOverlay,
  GridToolbar,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useRef, useState } from "react";

type DataGridProps = {
  rows: any;
  columns: Array<GridColDef>;
  loading?: boolean;
  updatepageData?: any;
  rowCount?: number;
  rowClick?: (params: any) => void;
  pageNo?: number;
  sortingColumn?: string;
  columnVisibility?: GridColumnVisibilityModel;
  setColumnVisibility?: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
};

const SUBMIT_FILTER_STROKE_TIME = 500;
const LoadingSkeleton = () => (
  <Box>
    {[...Array(10)].map((_, index) => {
      const key = `Key${index}`;
      return (
        <Box p={1} key={key}>
          <Skeleton variant="rectangular" sx={{ p: 2.2, borderRadius: 50 }} />
        </Box>
      );
    })}
  </Box>
);

const InputNumberInterval = (props: GridFilterInputValueProps) => {
  const { item, applyValue, focusElementRef = null } = props;
  const filterTimeout = useRef<any>();
  const [filterValueState, setFilterValueState] = useState<[string, string]>(
    item.value ?? ""
  );
  const [applying, setApplying] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      clearTimeout(filterTimeout.current);
    };
  }, []);

  useEffect(() => {
    const itemValue = item.value ?? [undefined, undefined];
    setFilterValueState(itemValue);
  }, [item.value]);

  const updateFilterValue = (lowerBound: string, upperBound: string) => {
    clearTimeout(filterTimeout.current);
    setFilterValueState([lowerBound, upperBound]);

    setApplying(true);
    filterTimeout.current = setTimeout(() => {
      setApplying(false);
      applyValue({ ...item, value: [lowerBound, upperBound] });
    }, SUBMIT_FILTER_STROKE_TIME);
  };

  const handleUpperFilterChange: TextFieldProps["onChange"] = (event) => {
    const newUpperBound = event.target.value;
    updateFilterValue(filterValueState[0], newUpperBound);
  };
  const handleLowerFilterChange: TextFieldProps["onChange"] = (event) => {
    const newLowerBound = event.target.value;
    updateFilterValue(newLowerBound, filterValueState[1]);
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "end",
        pl: "20px",
      }}
    >
      <TextField
        name="lower-bound-input"
        placeholder="From"
        label="From"
        variant="standard"
        value={Number(filterValueState[0])}
        onChange={handleLowerFilterChange}
        type="number"
        inputRef={focusElementRef}
        sx={{ mr: 2 }}
      />
      <TextField
        name="upper-bound-input"
        placeholder="To"
        label="To"
        variant="standard"
        value={Number(filterValueState[1])}
        onChange={handleUpperFilterChange}
        type="number"
        InputProps={applying ? { endAdornment: <Sync /> } : {}}
      />
    </Box>
  );
};

const CustomNoRowsOverlay = () => {
  const theme = useTheme();

  return (
    <GridOverlay>
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <Inventory fontSize="large" color="disabled" />
        <Typography
          variant="caption"
          fontWeight={"bold"}
          color={theme.palette.text.disabled}
        >
          No Rows
        </Typography>
      </Box>
    </GridOverlay>
  );
};

const TYDataGrid = ({
  rows,
  columns,
  loading,
  updatepageData,
  rowCount,
  rowClick,
  pageNo,
  sortingColumn,
  columnVisibility,
  setColumnVisibility,
}: DataGridProps) => {
  console.log('r', rows)
  const quantityOnlyOperators: GridFilterOperator[] = [
    {
      label: "Between",
      value: "between",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null;
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null;
        }

        return ({ value }) => {
          return (
            value !== null &&
            filterItem.value[0] <= value &&
            value <= filterItem.value[1]
          );
        };
      },
      InputComponent: InputNumberInterval,
    },
  ];
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [
      {
        id: 1,
        columnField: "AccelLR",
        value: undefined,
        operatorValue: "between",
      },
    ],
  });
  let nextRowId = 1;

  const columnsSet = useMemo(() => {
    const newColumns = [...columns];

    if (newColumns.length > 0) {
      const index = newColumns.findIndex((col) => col.field === "AccelLR");
      const quantityColumn = newColumns[index];

      newColumns[index] = {
        ...quantityColumn,
        filterOperators: quantityOnlyOperators,
      };
    }
    return newColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const getNextRowId = (): number => {
    const currentRowId = nextRowId;
    nextRowId += 1;
    return currentRowId;
  };

  // useEffect(() => {
  //   if (columnVisibility) {
  //     setVisibleColumns(columnVisibility);
  //   }
  // }, [columnVisibility]);

  // useEffect(() => {
  //   if (columns.length > 0) {
  //     let columnStatus = {};
  //     columns.forEach((col: GridColDef, index: number) => {
  //       if (index < 10) {
  //         columnStatus = {
  //           ...columnStatus,
  //           [col.field]: true,
  //         };
  //       } else {
  //         columnStatus = { ...columnStatus, [col.field]: false };
  //       }
  //     });
  //     if (setColumnVisibility) {
  //       setColumnVisibility(columnStatus);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <DataGrid
      rows={loading ? [] : rows}
      columns={columnsSet}
      pageSize={10}
      page={pageNo}
      rowCount={rowCount ?? rows.length}
      disableDensitySelector
      onCellClick={(params) =>
        params.field !== "Action" && rowClick && rowClick(params)
      }
      rowsPerPageOptions={[10]}
      onPageChange={(page) => {
        if (updatepageData && !loading) updatepageData(page);
      }}
      columnVisibilityModel={columnVisibility ?? undefined}
      onColumnVisibilityModelChange={(newVisible) => {
        if (setColumnVisibility) setColumnVisibility(newVisible);
      }}
      initialState={{
        sorting: {
          sortModel: sortingColumn
            ? [{ field: sortingColumn, sort: "desc" }]
            : undefined,
        },
      }}
      sx={{
        "& .MuiDataGrid-cell": {
          cursor: rowClick ? "pointer" : "default",
        },
        "& .MuiDataGrid-cell:focus": {
          outline: 0,
        },
      }}
      getRowId={getNextRowId}
      components={{
        Toolbar: GridToolbar,
        LoadingOverlay: LoadingSkeleton,
        NoRowsOverlay: CustomNoRowsOverlay,
      }}
      componentsProps={{
        toolbar: {
          csvOptions: { disableToolbarButton: true },
          printOptions: { disableToolbarButton: true },
        },
      }}
      disableColumnFilter={true}
      filterModel={filterModel}
      onFilterModelChange={(model) => setFilterModel(model)}
      disableSelectionOnClick
      autoHeight={!loading}
      loading={loading}
      style={{ padding: "1%", minHeight: loading ? "705px" : "auto" }}
    />
  );
};

export default TYDataGrid;
